import { type AgentsExitContext } from '@lib/analytics/agentsExit';
import { openAuthedChatUrl } from '@lib/network/chats';
import { SegmentType } from '@stores/Library/LibrarySearch';

const CHAT_URL = (process.env.CHAT_URL ?? 'https://chat.vibedit.ai/').replace(/\/$/, '');

/**
 * Opens the agent chat, optionally scoped to a library.
 * Use this for any navigation to the chat app (e.g. the Agents nav button).
 *
 * A short-lived exchange token is appended as `?token=…` so
 * chat.starzero.ai doesn't have to bounce back to app.starzero.ai for a
 * handoff. See `openAuthedChatUrl` in network/chats for the contract.
 *
 * NOTE: StarZero Agents does not currently support dealing with multiple
 * libraries at the same time. Pass a single library ID only.
 *
 * `exit` is required so the handoff is labelled in Mixpanel — see
 * `openAuthedChatUrl`.
 */
export const askAgent = (
  libraryId: string | undefined,
  target: '_self' | '_blank' = '_self',
  exit: AgentsExitContext,
): void => {
  const url = libraryId ? `${CHAT_URL}/?library=${libraryId}` : `${CHAT_URL}/`;
  // Derived fields go LAST: they describe the URL actually navigated to, so a
  // call site must not be able to contradict them via `exit`.
  void openAuthedChatUrl(url, target, { ...exit, libraryId });
};

/**
 * Opens the agent chat with the specified media in a new window.
 */
export const askAgentAboutMedia = (
  libraryId: string,
  mediaId: string | string[],
  exit: AgentsExitContext,
): void => {
  const ids = Array.isArray(mediaId) ? mediaId : [mediaId];
  const url = `${CHAT_URL}/?library=${libraryId}&media=${ids.join(',')}`;
  // Derived fields go LAST — see the note in `askAgent`.
  void openAuthedChatUrl(url, '_blank', {
    ...exit,
    libraryId,
    // Single-media exits get the id; bulk exits are summarised by count so we
    // never ship a 50-filename property blob.
    mediaId: ids.length === 1 ? ids[0] : undefined,
    mediaCount: ids.length,
  });
};

type MomentInfo = {
  libraryId: string;
  searchType: SegmentType;
  mediaId: string;
  beginTimestamp: number;
  endTimestamp: number;
};

function momentToString(moment: MomentInfo): string {
  return `${moment.mediaId}:${moment.searchType}:${moment.beginTimestamp}:${moment.endTimestamp}`;
}

/**
 * Opens the agent chat with the specified moments in a new window.
 */
export const askAgentAboutMoments = (
  libraryId: string,
  moments: MomentInfo | MomentInfo[],
  exit: AgentsExitContext,
): void => {
  const list = Array.isArray(moments) ? moments : [moments];
  const url = `${CHAT_URL}/?library=${libraryId}&moments=${list.map(momentToString).join(',')}`;
  // Derived fields go LAST — see the note in `askAgent`.
  void openAuthedChatUrl(url, '_blank', {
    ...exit,
    libraryId,
    momentCount: list.length,
    mediaId: list.length === 1 ? list[0]?.mediaId : undefined,
  });
};
